import React, { useContext, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchIcon from "@mui/icons-material/Search";
import { PatientModal } from "@components/Utils/Modals";
import { AccountContext } from "@/context/AccountContext";
import { useDebounce } from "@/hooks/useDebounce";
import {
  getPatients,
  getPatientById,
  getLocations,
} from "@features/patients/services/patientApi";
import "./NewProgramStartModal.css";

const EMPTY_PATIENT_FORM = {
  firstname: "",
  lastname: "",
  mail: "",
  diagnostic: "",
  sexuality: "",
  archived: 0,
  location: "",
};

const patientLabel = (patient) =>
  patient?.name ||
  [patient?.firstname, patient?.lastname].filter(Boolean).join(" ") ||
  "";

/**
 * The one entry point into program creation: asks which patient the program is
 * for, then how to build it.
 *
 * The app used to hide this behind the patient detail page, while the "Programme"
 * menu entry led to the template list — so the obvious route produced a template
 * that never reached a patient. This modal makes the obvious route the right one.
 */
export default function NewProgramStartModal({ show, onHide }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useContext(AccountContext);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [selectedPatient, setSelectedPatient] = useState(null);

  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientForm, setPatientForm] = useState(EMPTY_PATIENT_FORM);
  const [locations, setLocations] = useState([]);

  // Reset to the first step whenever the modal is reopened.
  useEffect(() => {
    if (!show) return;
    setQuery("");
    setSelectedPatient(null);
    setLoadError(false);
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    getPatients({ query: debouncedQuery, offset: 0, limit: 25 })
      .then((response) => {
        if (cancelled) return;
        setPatients(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        setPatients([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, debouncedQuery, reloadToken]);

  useEffect(() => {
    if (!show || !(userData?.client_id > 0)) return;
    getLocations()
      .then((response) => setLocations(response.data))
      .catch(() => {});
  }, [show, userData]);

  const openPatientModal = () => {
    setPatientForm(EMPTY_PATIENT_FORM);
    setShowPatientModal(true);
  };

  const handlePatientCreated = async (newPatientId) => {
    setShowPatientModal(false);
    // The id from the save is what makes this a patient's program; the fetched
    // record only supplies the name. Keep the id either way.
    const fallback = {
      id: newPatientId,
      name: [patientForm.firstname, patientForm.lastname]
        .filter(Boolean)
        .join(" "),
    };
    try {
      const { data } = await getPatientById(newPatientId);
      setSelectedPatient(data?.id ? data : fallback);
    } catch {
      setSelectedPatient(fallback);
    }
    setReloadToken((n) => n + 1);
  };

  const start = ({ openWizard }) => {
    // Without a real patient id the editor would fall through to the template
    // endpoint and quietly save a template instead of the patient's program.
    if (!selectedPatient?.id) return;
    onHide();
    navigate("/programs/create", {
      state: {
        account_patient_id: selectedPatient.id,
        patient_name: patientLabel(selectedPatient),
        open_wizard: openWizard,
      },
    });
  };

  const renderPatientStep = () => (
    <>
      <div className="nps-step-label">{t("programs.start.stepPatient")}</div>

      <div className="nps-search">
        <SearchIcon className="nps-search-icon" fontSize="small" />
        <input
          type="search"
          className="nps-search-input"
          value={query}
          autoFocus
          placeholder={t("programs.start.searchPlaceholder")}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <button type="button" className="nps-create-patient" onClick={openPatientModal}>
        <PersonAddAltIcon fontSize="small" />
        <span>{t("programs.start.createPatient")}</span>
      </button>

      <div className="nps-patient-list">
        {isLoading ? (
          <div className="nps-list-state">
            <Spinner animation="border" size="sm" style={{ color: "var(--main-color)" }} />
          </div>
        ) : null}

        {!isLoading && loadError ? (
          <div className="nps-list-state">
            <div>{t("programs.start.loadError")}</div>
            <Button
              variant="link"
              size="sm"
              onClick={() => setReloadToken((n) => n + 1)}
            >
              {t("programs.start.retry")}
            </Button>
          </div>
        ) : null}

        {!isLoading && !loadError && patients.length === 0 ? (
          <div className="nps-list-state">
            {query
              ? t("programs.start.noMatches")
              : t("programs.start.noPatients")}
          </div>
        ) : null}

        {!isLoading && !loadError
          ? patients.map((patient) =>
              patient?.id ? (
                <button
                  type="button"
                  key={patient.id}
                  className="nps-patient-row"
                  onClick={() => setSelectedPatient(patient)}
                >
                  <span className="nps-patient-name">{patientLabel(patient)}</span>
                  {patient.diagnostic ? (
                    <span className="nps-patient-meta">{patient.diagnostic}</span>
                  ) : null}
                </button>
              ) : null
            )
          : null}
      </div>
    </>
  );

  const renderModeStep = () => (
    <>
      <div className="nps-selected-patient">
        <span className="nps-patient-name">{patientLabel(selectedPatient)}</span>
        <button
          type="button"
          className="nps-change-patient"
          onClick={() => setSelectedPatient(null)}
        >
          {t("programs.start.changePatient")}
        </button>
      </div>

      <div className="nps-step-label">{t("programs.start.stepMode")}</div>

      <div className="nps-modes">
        <button
          type="button"
          className="nps-mode nps-mode--primary"
          onClick={() => start({ openWizard: true })}
        >
          <span className="nps-mode-badge">{t("programs.start.recommended")}</span>
          <span className="nps-mode-icon">✦</span>
          <span className="nps-mode-title">{t("programs.start.wizardTitle")}</span>
          <span className="nps-mode-text">{t("programs.start.wizardText")}</span>
        </button>
        <button
          type="button"
          className="nps-mode"
          onClick={() => start({ openWizard: false })}
        >
          <span className="nps-mode-icon">☰</span>
          <span className="nps-mode-title">{t("programs.start.manualTitle")}</span>
          <span className="nps-mode-text">{t("programs.start.manualText")}</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <Modal
        show={show && !showPatientModal}
        onHide={onHide}
        size="lg"
        centered
        scrollable
        className="new-program-start-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("programs.start.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPatient ? renderModeStep() : renderPatientStep()}
        </Modal.Body>
      </Modal>

      {showPatientModal && (
        <PatientModal
          show={showPatientModal}
          backdrop="static"
          onHide={() => setShowPatientModal(false)}
          status="n"
          data=""
          patientData={patientForm}
          locations={locations}
          handleOnChangeModal={(target, value) =>
            setPatientForm((prev) => ({ ...prev, [target]: value }))
          }
          onSaved={handlePatientCreated}
        />
      )}
    </>
  );
}
